<template>
  <div class="relative" ref="root">
    <button
      type="button"
      @click="open = !open"
      class="w-full px-2 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm flex items-center justify-between gap-1"
    >
      <span class="truncate" :class="selected.length ? 'text-dashboard-text' : 'text-dashboard-muted'">
        {{ summary }}
      </span>
      <ChevronDown class="w-4 h-4 flex-shrink-0 text-dashboard-muted" />
    </button>

    <div
      v-if="open"
      class="absolute z-20 mt-1 w-full min-w-[220px] bg-white border border-dashboard-border rounded-lg shadow-lg p-2 max-h-72 overflow-auto"
    >
      <!-- Bascule inclure / exclure -->
      <div v-if="showMode" class="flex items-center gap-1 mb-2">
        <button
          type="button"
          @click="$emit('update:mode', 'include')"
          :class="['flex-1 px-2 py-1 rounded-md text-xs font-medium border transition-colors',
                   mode !== 'exclude' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-dashboard-text border-dashboard-border']"
        >Inclure</button>
        <button
          type="button"
          @click="$emit('update:mode', 'exclude')"
          :class="['flex-1 px-2 py-1 rounded-md text-xs font-medium border transition-colors',
                   mode === 'exclude' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-dashboard-text border-dashboard-border']"
        >Exclure</button>
      </div>

      <input
        v-if="options.length > 8"
        v-model="query"
        type="text"
        placeholder="Filtrer…"
        class="w-full mb-2 px-2 py-1 border border-dashboard-border rounded-md text-xs"
      />

      <label
        v-for="opt in filteredOptions"
        :key="opt.value"
        class="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-dashboard-bg cursor-pointer text-sm"
      >
        <input type="checkbox" :value="opt.value" :checked="selected.includes(opt.value)" @change="toggle(opt.value)" />
        <span class="truncate">{{ opt.label }}</span>
      </label>
      <p v-if="!filteredOptions.length" class="text-xs text-dashboard-muted px-2 py-1">Aucune option.</p>

      <div v-if="selected.length" class="mt-2 pt-2 border-t border-dashboard-border flex justify-between items-center">
        <span class="text-xs text-dashboard-muted">{{ selected.length }} sélectionnée(s)</span>
        <button type="button" @click="clearAll" class="text-xs text-red-600 hover:underline">Tout retirer</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { ChevronDown } from 'lucide-vue-next'

interface Option { value: string; label: string }

const props = withDefaults(defineProps<{
  label: string
  options: Option[]
  modelValue: string[]
  mode?: string
  showMode?: boolean
}>(), { mode: 'include', showMode: false })

const emit = defineEmits<{
  (e: 'update:modelValue', v: string[]): void
  (e: 'update:mode', v: string): void
}>()

const root = ref<HTMLElement | null>(null)
const open = ref(false)
const query = ref('')

const selected = computed(() => props.modelValue || [])

const filteredOptions = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return props.options
  return props.options.filter((o) => o.label.toLowerCase().includes(q))
})

const summary = computed(() => {
  const n = selected.value.length
  if (!n) return props.label
  const prefix = props.showMode && props.mode === 'exclude' ? 'Exclure ' : ''
  if (n === 1) {
    const opt = props.options.find((o) => o.value === selected.value[0])
    return prefix + (opt?.label || '1 sélectionnée')
  }
  return `${prefix}${n} sélectionnées`
})

function toggle(value: string) {
  const set = new Set(selected.value)
  set.has(value) ? set.delete(value) : set.add(value)
  emit('update:modelValue', [...set])
}
function clearAll() {
  emit('update:modelValue', [])
}

function onClickOutside(e: MouseEvent) {
  if (open.value && root.value && !root.value.contains(e.target as Node)) open.value = false
}
onMounted(() => document.addEventListener('click', onClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside))
</script>
